// Todos view — supports two modes: board (kanban grouped by status)
// and list (table). Calendar mode from the Driicut original is omitted
// for the first BSP port; can be added later via the same /todos
// endpoint plus a week grid.
import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { tpApi } from "../api";
import { useResource } from "../hooks";
import {
  formatDateShort,
  isOverdue,
  priorityChipClass,
  statusChipClass,
} from "../utils";
import { Avatar } from "../components/Avatar";
import { TodoSheet } from "../sheets/TodoSheet";
import { usePlannerCreate, usePlannerRefreshKey } from "../sheets/PlannerCreateContext";
import {
  PlannerListFilters,
  emptyPlannerFilters,
  type PlannerListFilterState,
} from "../components/PlannerListFilters";
import { PlannerSavedFilters } from "../components/PlannerSavedFilters";
import { GroupByControl, type GroupByKey } from "../components/GroupByControl";
import { TodoCalendar } from "../components/TodoCalendar";
import {
  TODO_PRIORITY,
  TODO_STATUS,
  type Todo,
} from "../../../../../../shared/team-planner/schemas";
import type { PlannerScopeId } from "../../../../../../shared/team-planner/const";

type Mode = "board" | "list" | "calendar";

const COLUMNS: Array<{ id: Todo["status"]; label: string }> = [
  { id: "open", label: "Open" },
  { id: "in_progress", label: "In progress" },
  { id: "blocked", label: "Blocked" },
  { id: "done", label: "Done" },
];

function DroppableTodoColumn({
  id,
  label,
  count,
  children,
}: {
  id: string;
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`tp-kanban-column${isOver ? " tp-droppable-over" : ""}`}>
      <div className="tp-kanban-col-head">
        <span>{label}</span>
        <span className="tp-kanban-col-count">{count}</span>
      </div>
      {children}
    </div>
  );
}

function DraggableTodoCard({ todo, onOpen }: { todo: Todo; onOpen: (id: string) => void }) {
  const { setNodeRef, listeners, attributes, isDragging, transform } = useDraggable({
    id: todo.id,
    data: { kind: "todo" },
  });
  const style: React.CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : {};
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={isDragging ? "tp-dragging" : undefined}
      style={style}
      onClick={(e) => {
        if (isDragging) return;
        e.stopPropagation();
        onOpen(todo.id);
      }}
    >
      <TodoCard todo={todo} />
    </div>
  );
}

function TodoCard({ todo }: { todo: Todo }) {
  const overdue = isOverdue(todo.dueAt, todo.status);
  return (
    <div className="tp-card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
        <span className="tp-card-seq bsp-mono">T{todo.seq}</span>
        <span className={priorityChipClass(todo.priority)}>{todo.priority}</span>
      </div>
      <div className="tp-card-title">{todo.title}</div>
      {todo.rockTitle ? (
        <span
          style={{
            font: "600 11px/1.2 var(--bsp-font-sans)",
            color: "var(--bsp-blue)",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical" as const,
            overflow: "hidden",
          }}
          title={todo.rockTitle}
        >
          ↪ {todo.rockTitle}
        </span>
      ) : null}
      <div className="tp-card-meta">
        <span style={{ color: overdue ? "var(--bsp-red-fg)" : undefined }}>
          {todo.dueAt ? `Due ${formatDateShort(todo.dueAt)}` : "No due date"}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {todo.subtaskCounts.total > 0 ? (
            <span
              style={{
                fontFamily: "var(--bsp-font-mono)",
                fontSize: 10,
                color: "var(--bsp-fg-muted)",
              }}
            >
              {todo.subtaskCounts.done}/{todo.subtaskCounts.total}
            </span>
          ) : null}
          {todo.isPrivate ? (
            <span
              title="Private"
              style={{
                width: 14,
                height: 14,
                borderRadius: 3,
                background: "var(--ink-200)",
                color: "var(--ink-700)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 8,
                fontWeight: 800,
              }}
            >
              P
            </span>
          ) : null}
          {todo.primaryOwner ? <Avatar owner={todo.primaryOwner} /> : null}
        </span>
      </div>
    </div>
  );
}

export function TodosView({ scope }: { scope: PlannerScopeId }) {
  const [mode, setMode] = useState<Mode>("board");
  const [groupBy, setGroupBy] = useState<GroupByKey>("status");
  const [openId, setOpenId] = useState<string | null>(null);
  const openCreate = usePlannerCreate();
  const refreshKey = usePlannerRefreshKey();
  const [filters, setFilters] = useState<PlannerListFilterState>(emptyPlannerFilters);
  const todos = useResource(() => tpApi.listTodos(scope), [scope, refreshKey]);

  // Optimistic DnD: per-todo overrides keyed by the field the current
  // groupBy axis mutates. Map<todoId, Partial<Todo>>. Cleared on each
  // resource refresh so the server stays the source of truth.
  const [todoOverrides, setTodoOverrides] = useState<Map<string, Partial<Todo>>>(new Map());
  useEffect(() => {
    setTodoOverrides(new Map());
  }, [todos.data]);

  const filtered = useMemo(() => {
    if (!todos.data) return [];
    return todos.data
      .map((t) => {
        const o = todoOverrides.get(t.id);
        return o ? ({ ...t, ...o } as Todo) : t;
      })
      .filter((t) => {
        if (filters.status.length > 0 && !filters.status.includes(t.status)) return false;
        if (filters.priority.length > 0 && !filters.priority.includes(t.priority)) return false;
        return true;
      });
  }, [todos.data, filters, todoOverrides]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  async function onDragEnd(e: DragEndEvent) {
    const todoId = String(e.active.id);
    const destColumn = e.over?.id ? String(e.over.id) : null;
    if (!destColumn) return;
    const todo = filtered.find((t) => t.id === todoId);
    if (!todo) return;

    // Map groupBy → mutation + optimistic patch. Label axis is
    // skipped — replacing a label set destructively on drop has
    // ambiguous semantics (does the dragged card pick up the
    // destination label exclusively, additively?). Keep that
    // axis read-only for now.
    let patch: Partial<Todo> | null = null;
    let mutate: (() => Promise<unknown>) | null = null;

    if (groupBy === "status") {
      if (todo.status === destColumn) return;
      patch = { status: destColumn as Todo["status"] };
      mutate = () => tpApi.setTodoStatus(todoId, destColumn);
    } else if (groupBy === "priority") {
      if (todo.priority === destColumn) return;
      patch = { priority: destColumn as Todo["priority"] };
      mutate = () => tpApi.updateTodo(todoId, { priority: destColumn });
    } else if (groupBy === "owner") {
      const targetUserId = destColumn === "__unassigned__" ? null : destColumn;
      if (todo.primaryOwnerUserId === targetUserId) return;
      // Clear the hydrated `primaryOwner` block too so the optimistic
      // card avatar disappears the moment the drop lands. The server
      // refresh re-hydrates it for the new owner if there is one.
      patch = {
        primaryOwnerUserId: targetUserId,
        primaryOwner: targetUserId === null ? null : todo.primaryOwner,
      };
      // Dropping into Unassigned needs to remove the primary_owner
      // row entirely. The participant remove endpoint only accepts
      // co_owner / watcher roles, so we use a dedicated DELETE on
      // the primary path.
      mutate = targetUserId
        ? () =>
            tpApi.setPrimaryOwner("todo", todoId, {
              subjectType: "user",
              subjectId: targetUserId,
            })
        : () => tpApi.clearPrimaryOwner("todo", todoId);
    } else if (groupBy === "rock") {
      const targetRockId = destColumn === "__none__" ? null : destColumn;
      if (todo.rockId === targetRockId) return;
      patch = { rockId: targetRockId, milestoneId: null };
      mutate = () =>
        tpApi.updateTodo(todoId, { rockId: targetRockId, milestoneId: null });
    } else {
      return; // label / unsupported axis — no-op
    }

    setTodoOverrides((m) => {
      const next = new Map(m);
      next.set(todoId, { ...(next.get(todoId) ?? {}), ...patch! });
      return next;
    });
    try {
      await mutate!();
      todos.refresh();
    } catch {
      setTodoOverrides((m) => {
        const next = new Map(m);
        next.delete(todoId);
        return next;
      });
      todos.refresh();
    }
  }

  // groupBy controls the kanban column key. Status keeps the original
  // 4 columns; priority / owner / rock / label compute their own.
  const boardColumns = useMemo<Array<{ id: string; label: string; items: Todo[] }>>(() => {
    if (groupBy === "status") {
      return COLUMNS.map((c) => ({
        id: c.id,
        label: c.label,
        items: filtered.filter((t) => (t.status === "cancelled" ? "done" : t.status) === c.id),
      }));
    }
    if (groupBy === "priority") {
      return ["critical", "high", "medium", "low"].map((p) => ({
        id: p,
        label: p[0]!.toUpperCase() + p.slice(1),
        items: filtered.filter((t) => t.priority === p),
      }));
    }
    if (groupBy === "owner") {
      const buckets = new Map<string, { id: string; label: string; items: Todo[] }>();
      const KEY_UNASSIGNED = "__unassigned__";
      for (const t of filtered) {
        const ownerId = t.primaryOwnerUserId ?? KEY_UNASSIGNED;
        const label = t.primaryOwner?.displayName ?? "Unassigned";
        const bucket = buckets.get(ownerId) ?? { id: ownerId, label, items: [] };
        bucket.items.push(t);
        buckets.set(ownerId, bucket);
      }
      return Array.from(buckets.values()).sort((a, b) => a.label.localeCompare(b.label));
    }
    if (groupBy === "rock") {
      const buckets = new Map<string, { id: string; label: string; items: Todo[] }>();
      const KEY_NONE = "__none__";
      for (const t of filtered) {
        const k = t.rockId ?? KEY_NONE;
        const label = t.rockTitle ?? "Unlinked";
        const bucket = buckets.get(k) ?? { id: k, label, items: [] };
        bucket.items.push(t);
        buckets.set(k, bucket);
      }
      return Array.from(buckets.values()).sort((a, b) => a.label.localeCompare(b.label));
    }
    if (groupBy === "label") {
      const buckets = new Map<string, { id: string; label: string; items: Todo[] }>();
      const KEY_NONE = "__none__";
      for (const t of filtered) {
        const labels = (t.labels ?? []).length > 0 ? t.labels! : [KEY_NONE];
        for (const l of labels) {
          const labelText = l === KEY_NONE ? "No labels" : l;
          const bucket = buckets.get(l) ?? { id: l, label: labelText, items: [] };
          bucket.items.push(t);
          buckets.set(l, bucket);
        }
      }
      return Array.from(buckets.values()).sort((a, b) => a.label.localeCompare(b.label));
    }
    return [];
  }, [filtered, groupBy]);

  return (
    <>
      <div className="tp-toolbar">
        <div className="tp-toolbar-left">
          <div className="tp-scope">
            <button
              type="button"
              role="radio"
              aria-checked={mode === "board"}
              onClick={() => setMode("board")}
              className="tp-scope-btn"
            >
              Board
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === "list"}
              onClick={() => setMode("list")}
              className="tp-scope-btn"
            >
              List
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === "calendar"}
              onClick={() => setMode("calendar")}
              className="tp-scope-btn"
            >
              Calendar
            </button>
          </div>
          {mode === "board" ? (
            <GroupByControl value={groupBy} onChange={setGroupBy} />
          ) : null}
        </div>
        <div className="tp-toolbar-right">
          <button
            type="button"
            className="bsp-btn bsp-btn-primary"
            onClick={() => openCreate("todo")}
          >
            <Plus size={14} /> New to-do
          </button>
        </div>
      </div>

      <PlannerListFilters
        state={filters}
        onChange={setFilters}
        statusOptions={TODO_STATUS.map((s) => ({ id: s, label: s.replace("_", " ") }))}
        priorityOptions={TODO_PRIORITY.map((p) => ({ id: p, label: p }))}
      />
      <PlannerSavedFilters view="todos" current={filters} onApply={setFilters} />

      {todos.error ? (
        <p style={{ color: "var(--bsp-red-fg)" }}>Failed to load to-dos: {todos.error}</p>
      ) : null}

      {mode === "board" ? (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div className="tp-kanban">
            {boardColumns.map((col) => (
              <DroppableTodoColumn key={col.id} id={col.id} label={col.label} count={col.items.length}>
                {col.items.length === 0 ? (
                  <div className="tp-empty">No to-dos</div>
                ) : (
                  col.items.map((t) => (
                    <DraggableTodoCard key={t.id} todo={t} onOpen={setOpenId} />
                  ))
                )}
              </DroppableTodoColumn>
            ))}
          </div>
        </DndContext>
      ) : mode === "calendar" ? (
        <TodoCalendar todos={filtered} onOpen={setOpenId} />
      ) : (
        <div className="bsp-card" style={{ padding: 0 }}>
          <table className="bsp-table">
            <thead>
              <tr>
                <th style={{ width: 80 }}>Seq</th>
                <th>Title</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Owner</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} onClick={() => setOpenId(t.id)} style={{ cursor: "pointer" }}>
                  <td className="bsp-mono">T{t.seq}</td>
                  <td>{t.title}</td>
                  <td>
                    <span className={statusChipClass(t.status)}>{t.status.replace("_", " ")}</span>
                  </td>
                  <td>
                    <span className={priorityChipClass(t.priority)}>{t.priority}</span>
                  </td>
                  <td>
                    {t.primaryOwner ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <Avatar owner={t.primaryOwner} />
                        {t.primaryOwner.displayName}
                      </span>
                    ) : (
                      <span style={{ color: "var(--bsp-fg-muted)" }}>Unassigned</span>
                    )}
                  </td>
                  <td
                    style={{
                      color: isOverdue(t.dueAt, t.status) ? "var(--bsp-red-fg)" : undefined,
                    }}
                  >
                    {formatDateShort(t.dueAt)}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !todos.loading ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", color: "var(--bsp-fg-muted)" }}>
                    No to-dos match the current filter.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      {openId ? (
        <TodoSheet
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => todos.refresh()}
        />
      ) : null}
    </>
  );
}
